import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Save, Trash2, Loader2, Search, ImageIcon, RotateCcw, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 50;

/**
 * Tabla de gestión masiva de productos con:
 *  - Lazy loading (paginación incremental con Intersection Observer)
 *  - Edición debounced (500ms) que marca cambios pero no envía hasta "Guardar Todos"
 *  - Operaciones masivas: porcentaje (+10%) o valor fijo (+$1000) sobre Precio o Stock
 *  - URL rápida de imagen por fila
 *  - Botón "Guardar Todos los Cambios" → POST /admin/products/bulk-update consolidado
 */
const ProductsBulkTable = ({ categories, onAfterSave, onBulkDelete }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Estado de edición: { [productId]: { campo: nuevoValor } }
  const [pending, setPending] = useState({});
  // Archivos locales pendientes de subir: { [productId]: { file, previewUrl } }
  const [pendingFiles, setPendingFiles] = useState({});
  // Filas con drag-over activo
  const [dragOverId, setDragOverId] = useState(null);
  // Selección masiva
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Operación masiva
  const [bulkField, setBulkField] = useState('price');
  const [bulkType, setBulkType] = useState('percentage');
  const [bulkValue, setBulkValue] = useState('');
  const [saving, setSaving] = useState(false);

  const sentinelRef = useRef(null);
  const debounceRef = useRef({}); // setTimeout por campo
  const initialLoadRef = useRef(false);

  const loadPage = useCallback(async (resetSkip = false) => {
    if (loading) return;
    setLoading(true);
    try {
      const nextSkip = resetSkip ? 0 : skip;
      const params = new URLSearchParams({ skip: String(nextSkip), limit: String(PAGE_SIZE) });
      if (search.trim()) params.set('search', search.trim());
      if (categoryFilter) params.set('category_id', categoryFilter);
      const res = await axios.get(`${API}/admin/products/paginated?${params.toString()}`);
      const newItems = res.data.items || [];
      setTotal(res.data.total || 0);
      setItems(prev => resetSkip ? newItems : [...prev, ...newItems]);
      setSkip(nextSkip + newItems.length);
    } catch (e) {
      toast.error('Error cargando productos');
    } finally {
      setLoading(false);
    }
  }, [skip, loading, search, categoryFilter]);

  // Carga inicial y al cambiar filtros
  useEffect(() => {
    setSkip(0);
    setItems([]);
    initialLoadRef.current = false;
    // se dispara loadPage en el efecto de abajo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, categoryFilter]);

  useEffect(() => {
    if (!initialLoadRef.current && skip === 0 && items.length === 0 && !loading) {
      initialLoadRef.current = true;
      loadPage(true);
    }
  }, [skip, items.length, loading, loadPage]);

  // Intersection Observer (lazy loading)
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loading && items.length < total) {
        loadPage(false);
      }
    }, { rootMargin: '200px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [items.length, total, loading, loadPage]);

  // Marca un cambio pendiente (debounced 500ms para refrescar UI/estado consolidado)
  const queueChange = (productId, field, value) => {
    if (debounceRef.current[`${productId}_${field}`]) {
      clearTimeout(debounceRef.current[`${productId}_${field}`]);
    }
    // Optimista: actualiza la fila en pantalla inmediatamente
    setItems(prev => prev.map(p => p.id === productId ? { ...p, [field]: value } : p));
    debounceRef.current[`${productId}_${field}`] = setTimeout(() => {
      setPending(prev => ({
        ...prev,
        [productId]: { ...(prev[productId] || {}), [field]: value }
      }));
    }, 500);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    if (items.every(it => selectedIds.has(it.id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(it => it.id)));
    }
  };

  // Vista previa local del cambio masivo (NO envía al backend)
  const applyBulkLocal = () => {
    if (selectedIds.size === 0) {
      toast.error('Seleccioná al menos un producto');
      return;
    }
    const num = parseFloat(bulkValue);
    if (Number.isNaN(num)) {
      toast.error('Ingresá un valor numérico');
      return;
    }
    setItems(prev => prev.map(p => {
      if (!selectedIds.has(p.id)) return p;
      const current = Number(p[bulkField] ?? 0);
      let next = bulkType === 'percentage' ? current * (1 + num / 100) : current + num;
      if (bulkField === 'price') next = Math.max(0, Math.round(next * 100) / 100);
      else if (bulkField === 'stock') next = Math.max(0, Math.round(next));
      return { ...p, [bulkField]: next };
    }));
    setPending(prev => {
      const next = { ...prev };
      Array.from(selectedIds).forEach(id => {
        const item = items.find(it => it.id === id);
        if (!item) return;
        const current = Number(item[bulkField] ?? 0);
        let val = bulkType === 'percentage' ? current * (1 + num / 100) : current + num;
        if (bulkField === 'price') val = Math.max(0, Math.round(val * 100) / 100);
        else if (bulkField === 'stock') val = Math.max(0, Math.round(val));
        next[id] = { ...(next[id] || {}), [bulkField]: val };
      });
      return next;
    });
    toast.success(`Vista previa aplicada a ${selectedIds.size} producto(s). Recordá Guardar.`);
  };

  const resetChanges = () => {
    setPending({});
    // Liberar object URLs para no leakear memoria
    Object.values(pendingFiles).forEach(p => p?.previewUrl && URL.revokeObjectURL(p.previewUrl));
    setPendingFiles({});
    setSkip(0);
    setItems([]);
    initialLoadRef.current = false;
    toast.message('Cambios descartados');
  };

  // Captura un archivo local (click o drag&drop) → guarda preview y archivo, NO sube todavía
  const handleFileSelected = (productId, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('El archivo debe ser una imagen');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('La imagen supera 8 MB');
      return;
    }
    // Liberar preview previo si existía
    setPendingFiles(prev => {
      const previous = prev[productId];
      if (previous?.previewUrl) URL.revokeObjectURL(previous.previewUrl);
      return { ...prev, [productId]: { file, previewUrl: URL.createObjectURL(file) } };
    });
    // Marcar el producto como "con cambios pendientes" para activar Guardar
    setPending(prev => ({
      ...prev,
      [productId]: { ...(prev[productId] || {}), __image_pending: true }
    }));
  };

  const onDropImage = (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverId(null);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelected(productId, file);
  };

  const saveAll = async () => {
    const fileEntries = Object.entries(pendingFiles);
    const editedIds = new Set(Object.keys(pending));
    if (editedIds.size === 0 && fileEntries.length === 0) {
      toast.error('No hay cambios pendientes');
      return;
    }
    setSaving(true);
    try {
      // 1) Subir todos los archivos pendientes y mapear id → url devuelta
      const uploadedUrls = {};
      for (const [pid, { file }] of fileEntries) {
        const fd = new FormData();
        fd.append('file', file);
        const up = await axios.post(`${API}/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadedUrls[pid] = up.data?.url;
      }

      // 2) Consolidar updates: campos editados + image_url de los archivos subidos
      const allIds = new Set([...editedIds, ...Object.keys(uploadedUrls)]);
      const updates = Array.from(allIds).map(id => {
        const fields = { ...(pending[id] || {}) };
        delete fields.__image_pending; // flag interno, no enviar
        if (uploadedUrls[id]) fields.image_url = uploadedUrls[id];
        return { id, fields };
      }).filter(u => Object.keys(u.fields).length > 0);

      if (updates.length === 0) {
        toast.error('No hay cambios para guardar');
        setSaving(false);
        return;
      }

      const res = await axios.post(`${API}/admin/products/bulk-update`, { updates });
      toast.success(res.data.message || 'Guardado');

      // 3) Cleanup
      Object.values(pendingFiles).forEach(p => p?.previewUrl && URL.revokeObjectURL(p.previewUrl));
      setPendingFiles({});
      setPending({});
      setSkip(0);
      setItems([]);
      initialLoadRef.current = false;
      onAfterSave && onAfterSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar cambios');
    } finally {
      setSaving(false);
    }
  };

  const bulkDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`¿Eliminar ${selectedIds.size} producto(s) seleccionado(s)?`)) return;
    try {
      await axios.post(`${API}/admin/products/bulk-delete`, { product_ids: Array.from(selectedIds) });
      toast.success(`${selectedIds.size} producto(s) eliminados`);
      setSelectedIds(new Set());
      setPending({});
      setSkip(0);
      setItems([]);
      initialLoadRef.current = false;
      onBulkDelete && onBulkDelete();
    } catch (e) {
      toast.error('Error al eliminar productos');
    }
  };

  const pendingCount = Object.keys(pending).length;
  const allVisibleSelected = items.length > 0 && items.every(it => selectedIds.has(it.id));

  return (
    <div className="space-y-4" data-testid="products-bulk-table">
      {/* Filtros + búsqueda */}
      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar producto..."
            className="pl-9 bg-[#0a0a0a] border-[#333] text-white"
            data-testid="bulk-search-input"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="bg-[#0a0a0a] border border-[#333] text-white rounded px-3 py-2 text-sm"
          data-testid="bulk-category-filter"
        >
          <option value="">Todas las categorías</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <span className="text-xs text-gray-400">
          {items.length} / {total} cargados
        </span>
      </div>

      {/* Panel de operación masiva */}
      <div className="bg-[#1a1a1a] border border-[#E9FF60]/30 rounded-lg p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Campo</label>
            <select
              value={bulkField}
              onChange={(e) => setBulkField(e.target.value)}
              className="bg-[#0a0a0a] border border-[#333] text-white rounded px-3 py-2 text-sm"
              data-testid="bulk-op-field"
            >
              <option value="price">Precio</option>
              <option value="stock">Stock</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Tipo</label>
            <select
              value={bulkType}
              onChange={(e) => setBulkType(e.target.value)}
              className="bg-[#0a0a0a] border border-[#333] text-white rounded px-3 py-2 text-sm"
              data-testid="bulk-op-type"
            >
              <option value="percentage">Porcentaje (ej. +10%)</option>
              <option value="fixed">Valor fijo (ej. +1000)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Valor</label>
            <Input
              type="number"
              step="any"
              value={bulkValue}
              onChange={(e) => setBulkValue(e.target.value)}
              placeholder={bulkType === 'percentage' ? 'Ej: 10' : 'Ej: 1000'}
              className="w-32 bg-[#0a0a0a] border-[#333] text-white"
              data-testid="bulk-op-value"
            />
          </div>
          <Button
            onClick={applyBulkLocal}
            className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold"
            data-testid="bulk-apply-btn"
          >
            Aplicar a Selección ({selectedIds.size})
          </Button>
          {selectedIds.size > 0 && (
            <Button
              onClick={bulkDeleteSelected}
              variant="ghost"
              className="text-red-500 hover:text-red-400"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Eliminar selección
            </Button>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          La operación se previsualiza localmente. Confirmá con <strong>Guardar Todos los Cambios</strong>.
        </p>
      </div>

      {/* Tabla */}
      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0a0a0a] text-gray-400">
              <tr>
                <th className="px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    className="rounded"
                    data-testid="bulk-select-all"
                  />
                </th>
                <th className="px-3 py-2 text-left">Imagen</th>
                <th className="px-3 py-2 text-left">Nombre</th>
                <th className="px-3 py-2 text-left">Categoría</th>
                <th className="px-3 py-2 text-left">Precio</th>
                <th className="px-3 py-2 text-left">Stock</th>
                <th className="px-3 py-2 text-left">Disp.</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const isPending = !!pending[p.id];
                const isSelected = selectedIds.has(p.id);
                return (
                  <tr
                    key={p.id}
                    className={`border-t border-[#222] ${isPending ? 'bg-[#E9FF60]/5' : ''} ${isSelected ? 'bg-[#E9FF60]/10' : ''}`}
                    data-testid={`bulk-row-${p.id}`}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(p.id)}
                        className="rounded"
                        data-testid={`bulk-select-${p.id}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverId(p.id); }}
                        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverId(null); }}
                        onDrop={(e) => onDropImage(e, p.id)}
                        className={`flex items-center gap-2 rounded p-1.5 border border-dashed transition ${dragOverId === p.id ? 'border-[#E9FF60] bg-[#E9FF60]/10' : 'border-[#333]'}`}
                        data-testid={`bulk-image-dropzone-${p.id}`}
                      >
                        {pendingFiles[p.id]?.previewUrl ? (
                          <img src={pendingFiles[p.id].previewUrl} alt="preview" className="w-12 h-12 object-cover rounded ring-2 ring-[#E9FF60]" />
                        ) : p.image_url ? (
                          <img src={p.image_url} alt={p.name} className="w-12 h-12 object-cover rounded" />
                        ) : (
                          <div className="w-12 h-12 rounded bg-[#0a0a0a] flex items-center justify-center text-gray-600">
                            <ImageIcon className="w-5 h-5" />
                          </div>
                        )}
                        <label
                          className="flex flex-col items-start gap-0.5 cursor-pointer text-xs select-none"
                          data-testid={`bulk-image-label-${p.id}`}
                        >
                          <span className="inline-flex items-center gap-1 text-[#E9FF60] hover:underline">
                            <Upload className="w-3 h-3" />
                            {pendingFiles[p.id] ? 'Reemplazar' : 'Subir imagen'}
                          </span>
                          <span className="text-gray-500 text-[10px]">o arrastrá aquí</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleFileSelected(p.id, e.target.files?.[0])}
                            data-testid={`bulk-image-input-${p.id}`}
                          />
                        </label>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        defaultValue={p.name || ''}
                        onChange={(e) => queueChange(p.id, 'name', e.target.value)}
                        className="w-44 bg-[#0a0a0a] border-[#333] text-white"
                        data-testid={`bulk-name-${p.id}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        defaultValue={p.category_id || ''}
                        onChange={(e) => queueChange(p.id, 'category_id', e.target.value)}
                        className="bg-[#0a0a0a] border border-[#333] text-white rounded px-2 py-1 text-xs"
                        data-testid={`bulk-cat-${p.id}`}
                      >
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={p.price ?? ''}
                        onChange={(e) => queueChange(p.id, 'price', parseFloat(e.target.value) || 0)}
                        className="w-24 bg-[#0a0a0a] border-[#333] text-white"
                        data-testid={`bulk-price-${p.id}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        value={p.stock ?? ''}
                        onChange={(e) => queueChange(p.id, 'stock', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                        placeholder="∞"
                        className="w-20 bg-[#0a0a0a] border-[#333] text-white"
                        data-testid={`bulk-stock-${p.id}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        defaultChecked={p.available !== false}
                        onChange={(e) => queueChange(p.id, 'available', e.target.checked)}
                        className="rounded"
                        data-testid={`bulk-available-${p.id}`}
                      />
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && !loading && (
                <tr><td colSpan="7" className="text-center py-8 text-gray-500">No hay productos</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Sentinel para lazy loading */}
        <div ref={sentinelRef} className="h-8 flex items-center justify-center text-xs text-gray-500">
          {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          {!loading && items.length < total && 'Desplazá para cargar más...'}
          {!loading && items.length >= total && total > 0 && 'Fin de la lista'}
        </div>
      </div>

      {/* Footer sticky con acción de guardado */}
      <div className="sticky bottom-0 bg-[#0a0a0a] border-t border-[#333] py-3 px-4 -mx-4 flex items-center justify-between" data-testid="bulk-save-bar">
        <span className="text-sm text-gray-400">
          {pendingCount > 0 ? (
            <span className="text-[#E9FF60] font-semibold">{pendingCount} producto(s) con cambios pendientes</span>
          ) : 'Sin cambios pendientes'}
        </span>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={resetChanges}
            disabled={pendingCount === 0 || saving}
            className="text-gray-400"
            data-testid="bulk-reset-btn"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Descartar
          </Button>
          <Button
            onClick={saveAll}
            disabled={pendingCount === 0 || saving}
            className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold"
            data-testid="bulk-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar Todos los Cambios
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductsBulkTable;
